<template>
  <div class="main-area">
    <loading-screen v-if="!appReady" :text="uiLoadingMessage" />

    <router-view v-if="appReady" />

    <onboarding-profile-missing-modal />
    <notes-modal />
    <send-message-modal />
    <notifications-feed />
    <recommendations-feed />
    <recommendation-reasons />
    <patient-summary-side-bar />
    <timeout-manager />
    <chat-popup v-if="isAuthenticated && !isDemo" />

    <theme-selector v-if="showThemeSelector" :current-theme="currentTheme" @change-theme="selectPortalTheme" />
  </div>
</template>

<script>

import { ref, computed, onBeforeMount, onMounted } from 'vue'
import store from '@/store'

// Composables
import useExercisesAssets from '@/composables/use-exercises-assets'
// Configs
import { VERSION_FILE } from '@/scripts/app-configs/constants'

// Tools
import ACLModule from '@/scripts/integrations/acl-module'
import { selectTheme } from '@ui-kit/themes'

// Components
// -- Global Modals
import OnboardingProfileMissingModal from '@/components/modals/onboarding-profile-missing-modal.vue'
import NotesModal from '@/components/modals/NotesModal.vue'
import SendMessageModal from '@/components/modals/SendMessageModal.vue'
import NotificationsFeed from '@/components/notifications/NotificationsFeed.vue'
import RecommendationsFeed from '@/components/recommendations/RecommendationsFeed.vue'
import RecommendationReasons from '@/components/recommendations/RecommendationReasons.vue'
import PatientSummarySideBar from '@/components/modals/patient-summary-side-bar.vue'
import TimeoutManager from '@/components/timeout-manager.vue'
import ChatPopup from '@/views/chat/components/ChatPopup.vue'
// -- Generic
import LoadingScreen from '@/components/feedbacks/LoadingScreen.vue'
import ThemeSelector from '@sword-health/web-ui-kit/src/dev-utils/theme-selector/theme-selector.vue'
import * as config from './scripts/app-configs/config'

export default {
  name: 'App',
  components: {
    LoadingScreen,
    OnboardingProfileMissingModal,
    NotesModal,
    SendMessageModal,
    NotificationsFeed,
    RecommendationsFeed,
    RecommendationReasons,
    PatientSummarySideBar,
    TimeoutManager,
    ThemeSelector,
    ChatPopup,
  },
  setup() {
    const currentTheme = ref('sword')
    const showThemeSelector = process.env.NODE_ENV === 'development' && false

    const appReady = computed(() => store.getters['system/uiReady'])
    const uiLoadingMessage = computed(() => store.getters['system/uiLoadingMessage'])
    const isAuthenticated = computed(() => store.getters['user/authentication/isAuthenticated'])

    const isDemo = config.get('DEMO_MODE') === 'true'
    const userExists = computed(() => store.getters['user/userExist'])
    const exerciseAssetsVersion = config.get('ASSETS_VERSION')

    useExercisesAssets(exerciseAssetsVersion, { fetchEnabled: userExists })

    const selectPortalTheme = (themeName) => {
      selectTheme(themeName)
      currentTheme.value = themeName
    }

    onMounted(() => {
      selectPortalTheme(currentTheme.value)

      if (userExists.value) {
        store.dispatch('user/authentication/loginSuccess')
      }
    })

    onBeforeMount(() => {
      store.commit('system/setAppVersion', { context: 'build', versionObj: VERSION_FILE })
      store.commit('user/authentication/loadStoredAuthTokens')

      ACLModule.requestUserAccess().catch(() => {
        store.commit('system/setUIReadyState', true)
        ACLModule.redirectToSignIn()
      })
    })

    return {
      appReady,
      isAuthenticated,
      isDemo,
      currentTheme,
      showThemeSelector,
      uiLoadingMessage,
      selectPortalTheme,
    }
  },
  mounted() {
    this.$zendesk.init()
  },
  beforeDestroy() {
    this.$zendesk.hide()
  },
}
</script>
